import { gql, useMutation } from "@apollo/client";
import moment from "moment";
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { ProfileImage } from "src/Components/Segments";
import { AppText, FlexDiv } from "src/Components/UI";
import { PhoenixAppButton, PhoenixIcon, PhoenixMultiSelect, PhoenixTextArea } from "src/Components/UI/Phoenix";
import { phone_outgoing, pin } from "src/images/NewDesign";
import { OptionItem } from "src/types";
import { formatPhoneNumber } from "src/utils/format";
import { useDebounce, useFirstRender } from "src/utils/hooks";
import { theme } from "src/utils/theme";
import { errorToast, successToast } from "src/utils/toast";
import styled from "styled-components";

interface INotesTab {
  leadData: any;
  associatedContacts?: any[];

  noteHistoryData?: any[];
  maxHeight?: number;

  contactFilter?: string;
  setContactFilter?: Dispatch<SetStateAction<string | undefined>>;
}

const ADD_LEAD_NOTE = gql`
  mutation addNote($lead_id: String!, $note: String!) {
    addLeadNote(lead_id: $lead_id, note: $note) {
      id
      notes
      created_at
    }
  }
`;
const UPDATE_LEAD_NOTE = gql`
  mutation updateLeadNote($updateLeadNoteId: String!, $isPinnedToTop: Boolean!) {
    updateLeadNote(id: $updateLeadNoteId, is_pinned_to_top: $isPinnedToTop) {
      id
    }
  }
`;

export const NotesTab: React.FC<INotesTab> = ({
  leadData,
  associatedContacts,
  noteHistoryData,
  maxHeight,
  contactFilter,
  setContactFilter,
}) => {
  const sortedNoteHistoryData = useMemo(
    () => [...(noteHistoryData || [])].sort((a, b) => Number(b?.is_pinned_to_top) - Number(a?.is_pinned_to_top)),
    [noteHistoryData],
  );

  const contactOptions = useMemo(
    () => [
      { label: "All", value: undefined },
      {
        label:
          leadData?.first_name && leadData?.last_name ? `${leadData?.first_name} ${leadData?.last_name}` : "No Name",
        value: leadData?.id,
      },
      ...(!!associatedContacts?.length
        ? associatedContacts?.map((item: any) => {
            return {
              label: item?.first_name && item?.last_name ? `${item?.first_name} ${item?.last_name}` : "No Name",
              value: item?.id,
            };
          })
        : []),
    ],
    [leadData, associatedContacts],
  );
  const contactValue = useMemo(() => {
    const contactOption = contactOptions.find((option) => option.value === contactFilter);
    return contactOption?.value ? contactOption : undefined;
  }, [contactOptions, contactFilter]);

  return (
    <Wrapper maxHeight={maxHeight}>
      <Container>
        <FlexDiv
          justify="space-between"
          align="center"
          padding="8px 0px"
          style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: theme.surface.brand.primary }}
        >
          <AppText fontSize={12} fontWeight={500} lineHeight={18} color={theme.text.neutral.tertiary}>
            {noteHistoryData?.length || 0} Results
          </AppText>
          <PhoenixMultiSelect
            isMulti={false}
            name="contact"
            options={contactOptions}
            placeholder="Contact"
            onChange={(e: OptionItem) => setContactFilter && setContactFilter(e.value as string)}
            value={contactValue}
            isClearable={false}
            marginBottom={false}
            error={false}
            ghostSelect
            ghostSelectAppThemeOverride="Light"
            width={80}
            height={24}
            minHeight={24}
            optionsContainerWidth={120}
            style={{ overflow: "hidden" }}
            lineHeight="10px"
            pointer
            menuBorder
            removeMenuPadding
            menuPortal
          />
        </FlexDiv>

        <NoteInput contactOptions={contactOptions.slice(1)} leadId={leadData?.id} />

        {sortedNoteHistoryData?.map((noteData) => (
          <Note key={noteData.id} noteData={noteData} />
        ))}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ maxHeight?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;

  height: 100%;
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : "100%")};

  overflow-y: auto;
`;

const Container = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 70%;
  max-width: 784px;
  min-width: 330px;

  padding-bottom: 32px;
`;

interface INote {
  noteData: any;
}

export const Note = React.memo<INote>(({ noteData }) => {
  const [pinned, setPinned] = useState<boolean>(noteData?.is_pinned_to_top || false);

  const [updateLeadNote] = useMutation(UPDATE_LEAD_NOTE, {
    onError({ message }) {
      errorToast(message);
    },
  });

  const handlePinClick = useCallback(() => {
    setPinned((prev) => !prev);
  }, [pinned, setPinned]);

  const isFirstRender = useFirstRender();

  useDebounce(
    () => {
      !isFirstRender &&
        updateLeadNote({
          variables: {
            updateLeadNoteId: noteData?.id,
            isPinnedToTop: pinned,
          },
        });
    },
    [pinned],
    500,
  );

  return (
    <NoteContainer>
      <NoteHeader>
        <FlexDiv align="center" gap={8}>
          <ProfileImage profileImage={noteData?.user?.profile_image} diameter={24} borderDiameter={24} />
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.primary}>
            {moment(parseInt(noteData?.created_at)).format("M/DD/YYYY")}{" "}
            <span style={{ color: theme.text.neutral.secondary }}>
              {moment(parseInt(noteData?.created_at)).format("h:mm A")}
            </span>
          </AppText>
        </FlexDiv>

        <FlexDiv align="center" gap={8}>
          <FlexDiv align="center" gap={8}>
            <PhoenixIcon
              svg={phone_outgoing}
              size={16}
              color={theme.icon.brand.default}
              hoverColor={theme.icon.brand.default}
            />
            <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.primary}>
              {formatPhoneNumber(noteData?.user?.phone_number)}
            </AppText>
          </FlexDiv>

          <PinButton onClick={handlePinClick} pinned={pinned}>
            <PhoenixIcon
              svg={pin}
              size={16}
              color={pinned ? theme.icon.neutral.inverse : theme.icon.neutral.default}
              hoverColor={pinned ? theme.icon.neutral.inverse : theme.icon.neutral.default}
              pointer
            />
          </PinButton>
        </FlexDiv>
      </NoteHeader>
      <NoteBody>
        <AppText fontSize={12} fontWeight={400} lineHeight={18}>
          {noteData?.notes}
        </AppText>
      </NoteBody>
    </NoteContainer>
  );
});

const NoteContainer = styled.div`
  width: 100%;
  height: fit-content;

  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;

  background-color: ${theme.fill.neutral.primary};
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;

  width: 100%;
  height: 40px;
  padding: 8px;

  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const NoteBody = styled.div`
  padding: 8px 16px;
  min-height: 84px;
  height: fit-content;
`;

const PinButton = styled.button<{ pinned: boolean }>`
  border: none;
  padding: 0;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;
  border-radius: 24px;

  background: ${({ pinned }) => (pinned ? "linear-gradient(90deg, #204FC7 0%, #0282F9 100%)" : "transparent")};

  :hover {
    background-color: ${({ pinned }) => !pinned && theme.fill.neutral.secondary};
  }
`;

export const NoteInput: React.FC<{ contactOptions: any[]; leadId: string }> = ({ contactOptions, leadId }) => {
  const [text, setText] = useState<string>("");
  const [selectedContact, setSelectedContact] = useState<string | undefined>(undefined);

  const [addLeadNote, { loading: loadingAddLeadNote }] = useMutation(ADD_LEAD_NOTE, {
    onCompleted() {
      successToast("Note added successfully");
      setText("");
    },
    onError({ message }) {
      errorToast(message);
    },
    refetchQueries: ["fetchNoteHistory"],
  });

  return (
    <NoteContainer>
      <FlexDiv justify="space-between" align="center" padding="8px">
        <PhoenixMultiSelect
          isMulti={false}
          name="contact"
          options={contactOptions}
          placeholder="Assign to Contact"
          onChange={(e: OptionItem) => setSelectedContact(e.value as string)}
          value={contactOptions.find((option) => option.value === selectedContact)}
          isClearable={false}
          marginBottom={false}
          error={false}
          ghostSelect
          ghostSelectAppThemeOverride="Light"
          width={132}
          height={24}
          minHeight={24}
          optionsContainerWidth={132}
          style={{ overflow: "hidden" }}
          lineHeight="10px"
          pointer
          menuBorder
          removeMenuPadding
          menuPortal
        />
        <FlexDiv gap={8}>
          <PhoenixAppButton
            buttonType="ghost-small"
            variant="danger-outline"
            disabled={!text.length}
            onClick={() => setText("")}
          >
            <AppText
              fontSize={10}
              fontWeight={600}
              uppercase
              letterSpacing={1}
              color={theme.buttontext.danger_outline.default}
            >
              Cancel
            </AppText>
          </PhoenixAppButton>
          <PhoenixAppButton
            buttonType="ghost-small"
            disabled={!text.length || loadingAddLeadNote}
            onClick={() => addLeadNote({ variables: { lead_id: selectedContact || leadId, note: text } })}
          >
            <AppText
              fontSize={10}
              fontWeight={600}
              uppercase
              letterSpacing={1}
              color={theme.buttontext.brand_outline.default}
            >
              Save
            </AppText>
          </PhoenixAppButton>
        </FlexDiv>
      </FlexDiv>

      <PhoenixTextArea
        name="note"
        height={116}
        placeholder="Write a note."
        value={text}
        onChange={(e) => setText(e.target.value)}
        fixedSize
        borderColor="transparent"
        cursorColor={theme.text.neutral.secondary}
        hideFocus
      />
    </NoteContainer>
  );
};
